import React, {useMemo} from "react"
import {SchoolClass, SchoolClassCheckIn, SchoolClassContentPiece} from "models"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component"
import ApiMakerConfig from "@kaspernj/api-maker/build/config"
import AppHistory from "shared/history"
import CkeditorPresentation from "ckeditor/presentation"
import DogBackground from "components/user/class-profile/dog-background.png"
import HayaDatePickerInput from "haya-date-picker/build/date-picker/input"
import I18n from "shared/i18n"
import isUserA from "components/users/is-a"
import memo from "set-state-compare/build/memo"
import moment from "shared/moment"
import MoodSimpleLineChart from "components/score-factor-groups/mood-simple-line-chart"
import Params from "@kaspernj/api-maker/build/params"
import ProUpgradeOverlay from "components/pro/upgrade-overlay"
import Routes from "shared/routes"
import SanitizedHtml from "shared/sanitized-html"
import SchoolClassSelect from "./class-profile-route/school-class-select"
import ScoreFactorChartSlide from "components/user/school-classes/insights/score-factor-chart-slide"
import ScoreFactorGroupsMoodBarChart from "components/score-factor-groups/mood-bar-chart"
import {Text} from "shared/base"
import UnicornCountChart from "components/user/unicorn/count-chart"
import UnicornEffects from "components/user/unicorn/unicorn-effects"
import useAnythingPro from "components/subscriptions/use-anything-pro"
import useCurrentUser from "@kaspernj/api-maker/build/use-current-user"
import {useFrontLayout} from "components/front-layout"
import useHasPro from "components/subscriptions/use-has-pro"
import useI18n from "i18n-on-steroids/src/use-i18n.mjs"
import useModel from "@kaspernj/api-maker/build/use-model"
import useQueryParams from "on-location-changed/build/use-query-params"
import UtilsCard from "components/utils/card"
import {View} from "react-native"

export default memo(shapeComponent(class RoutesUserClassProfileStatus extends ShapeComponent {
  setup() {
    const currentUser = useCurrentUser()
    const queryParams = useQueryParams()
    const {locale, t} = useI18n({namespace: "js.routes.user.class_profile"})
    const {hasPro} = useHasPro({schoolClassId: queryParams.school_class_id, user: currentUser})
    const {anythingPro} = useAnythingPro()

    const dateFrom = useMemo(() => {
      if (queryParams.date_from) {
        return new Date(Date.parse(queryParams.date_from))
      } else {
        return moment().subtract(12, "months").toDate()
      }
    }, [queryParams.date_from])

    const dateTo = useMemo(() => {
      if (queryParams.date_to) {
        return new Date(Date.parse(queryParams.date_to))
      } else {
        return new Date()
      }
    }, [queryParams.date_to])

    const {schoolClass, schoolClassId} = useModel(SchoolClass, {
      active: Boolean(queryParams.school_class_id),
      loadByQueryParam: ({queryParams}) => queryParams.school_class_id,
      select: {
        SchoolClass: ["hasProSubscription", "id", "interpretedName"]
      }
    })

    const schoolClassCheckInsQuery = useMemo(
      () => SchoolClassCheckIn.ransack({
        created_at_gteq: I18n.strftime("%Y-%m-%d", dateFrom),
        created_at_lteq: I18n.strftime("%Y-%m-%d", dateTo),
        school_class_id_eq: schoolClassId
      }),
      [dateFrom, dateTo, schoolClassId]
    )

    this.setInstance({
      anythingPro,
      currentUser,
      dateFrom,
      dateTo,
      hasPro,
      schoolClass,
      schoolClassCheckInsQuery,
      schoolClassIds: useMemo(
        () => {
          if (schoolClass) {
            return [schoolClass.id()]
          }
        },
        [schoolClass?.id()]
      ),
      t
    })
    this.useStates({schoolClassContentPieces: null})

    useMemo(() => {
      if (schoolClass) {
        this.loadSchoolClassContentPieces()
      } else {
        this.setState({schoolClassContentPieces: null})
      }
    }, [currentUser?.id(), locale, schoolClass?.id()])

    useFrontLayout()?.setState({active: "user-class-profile"})
  }

  async loadSchoolClassContentPieces() {
    const schoolClassContentPieces = await SchoolClassContentPiece
      .ransack({
        s: "content_piece_type_current_translation_name",
        school_class_id_eq: this.tt.schoolClass.id(),
        latest_for_school_class: true
      })
      .preload("content_piece_type")
      .select({
        ContentPieceType: ["name"],
        SchoolClassContentPiece: ["content", "id"]
      })
      .toArray()

    this.setState({schoolClassContentPieces})
  }

  render() {
    const {anythingPro, currentUser, schoolClass} = this.tt

    return (
      <View dataSet={this.rootViewDataSet ||= {route: "user/class-profile"}}>
        {(() => {
          if (!anythingPro || schoolClass?.hasProSubscription() === false) {
            return <ProUpgradeOverlay onRequestClose={this.tt.onUpgradeOverlayRequestClose} />
          } else if (isUserA(currentUser, "tester")) {
            return this.content()
          } else {
            return this.comingSoonContent()
          }
        })()}
      </View>
    )
  }

  comingSoonContent() {
    const {t} = this.tt

    return (
      <>
        <div className="coming-soon-container" style={{backgroundImage: `url('${DogBackground}')`}}>
          <div className="coming-soon-text-container">
            <div className="coming-soon-header">
              {t(".header")}
            </div>
            <div className="coming-soon-body">
              <SanitizedHtml html={t(".body")} />
            </div>
          </div>
        </div>
      </>
    )
  }

  content() {
    const {dateFrom, dateTo, schoolClass, schoolClassCheckInsQuery, schoolClassIds, t} = this.tt

    return (
      <View>
        <View style={{flexDirection: "row"}}>
          <View style={{width: "60%"}} />
          <View style={{width: "20%"}}>
            <SchoolClassSelect />
          </View>
          <View style={{width: "20%", paddingLeft: 15}}>
            <HayaDatePickerInput
              datePickerProps={this.cache("hayaDatePickerInputdatePickerProps", {
                dateFrom,
                dateTo,
                mode: "dateRange",
                onSelect: this.tt.onDateRangeSelect
              }, [dateFrom, dateTo])}
              styles={this.hayaDatePickerInputStyles ||= {
                text: {
                  marginLeft: 12,
                  color: "#666",
                  fontSize: 17
                },
                view: {
                  paddingTop: 17,
                  paddingRight: 26,
                  paddingBottom: 17,
                  paddingLeft: 26,
                  backgroundColor: "#fff",
                  border: "1px solid #dcdcdc",
                  borderRadius: 10
                }
              }}
            />
          </View>
        </View>
        {schoolClass &&
          <>
            <UtilsCard header={schoolClass.interpretedName()} style={{marginTop: 15}}>
              <UtilsCard header={t(".mood")}>
                <View style={{width: 877}}>
                  <MoodSimpleLineChart dateFrom={dateFrom} dateTo={dateTo} schoolClass={schoolClass} />
                </View>
              </UtilsCard>
            </UtilsCard>
            <View style={{marginTop: 15}}>
              <UnicornEffects dateFrom={dateFrom} dateTo={dateTo} schoolClassIds={schoolClassIds} />
            </View>
            <View style={{marginTop: 15}}>
              <UtilsCard style={{maxWidth: 700}}>
                <View style={{width: "calc(100% + 50px)", marginLeft: -50}}>
                  <ScoreFactorGroupsMoodBarChart schoolClass={schoolClass} schoolClassCheckInsQuery={schoolClassCheckInsQuery} />
                </View>
              </UtilsCard>
            </View>
            <View style={{marginTop: 15}}>
              <UnicornCountChart dateFrom={dateFrom} dateTo={dateTo} schoolClassIds={schoolClassIds} />
            </View>
            <View style={{marginTop: 15}}>
              <ScoreFactorChartSlide dateFrom={dateFrom} dateTo={dateTo} schoolClass={schoolClass} />
            </View>
          </>
        }
        {this.s.schoolClassContentPieces?.map((schoolClassContentPiece) =>
          <View
            key={schoolClassContentPiece.id()}
            style={this.contentPieceViewStyle ||= {
              marginTop: 15,
              backgroundColor: "#5253ff",
              borderRadius: 10,
              padding: 20
            }}
          >
            <Text style={this.contentPieceTypeNameStyle ||= {color: "#fff", fontSize: 24, fontWeight: "bold"}}>
              {schoolClassContentPiece.contentPieceType().name()}
            </Text>
            <View
              style={this.contentPieceCkeditorPresentationContainerStyle ||= {
                flex: 0,
                marginTop: 10,
                padding: 20,
                backgroundColor: "#fff",
                borderRadius: 34
              }}
            >
              <CkeditorPresentation html={schoolClassContentPiece.content()} />
            </View>
          </View>
        )}
      </View>
    )
  }

  onUpgradeOverlayRequestClose = ({reason} = {}) => {
    if (reason !== "stay-basic") {
      return
    }

    if (!this.tt.anythingPro || this.tt.schoolClass?.hasProSubscription() === false) {
      this.goBackOrDashboard()
    }
  }

  goBackOrDashboard() {
    const history = ApiMakerConfig.getHistory()

    if (history?.back) {
      history.back()

      return
    }

    if (history?.goBack) {
      history.goBack()

      return
    }

    if (history?.go && typeof history.length === "number" && history.length > 1) {
      history.go(-1)

      return
    }

    if (typeof window !== "undefined" && window.history?.length > 1) {
      window.history.back()

      return
    }

    AppHistory.push(Routes.userRootPath())
  }

  onDateRangeSelect = ({fromDate, toDate}) => {
    Params.changeParams({date_from: I18n.strftime("%Y-%m-%d", fromDate), date_to: I18n.strftime("%Y-%m-%d", toDate)})
  }
}))
